import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Food, Meal } from '@jungles/shared';
import { ApiError, api } from '../lib/api';
import { useToast } from '../app/toast';
import { hapticSuccess } from '../lib/telegram';
import { CloseIcon, FlashIcon } from '../components/icons';
import { createBarcodeReader, isProductBarcode, type BarcodeReader } from '../lib/barcode';
import type { ScanPrefill } from './FoodForm';

/** N2 · Сканирование штрихкода и этикетки (SPEC §5, §8). */

type Mode = 'barcode' | 'label';
type Problem = { kind: 'not_found' | 'unavailable' | 'ocr'; message: string; code?: string };

export function ScanScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const meal = (params.get('meal') ?? 'snack') as Meal;
  const date = params.get('date') ?? '';
  const query = `meal=${meal}&date=${date}`;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanning = useRef(false);

  const readerRef = useRef<BarcodeReader | null>(null);
  const [engine, setEngine] = useState<'native' | 'wasm' | 'none' | 'loading'>('loading');
  const [mode, setMode] = useState<Mode>('barcode');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [manualCode, setManualCode] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const stop = useCallback(() => {
    scanning.current = false;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const handleFound = useCallback(
    (food: Pick<Food, 'name' | 'brand' | 'barcode'> & { kcal100: number | null; p100: number | null; f100: number | null; c100: number | null }, incomplete = false) => {
      const prefill: ScanPrefill = {
        name: food.name,
        brand: food.brand,
        grams: 100,
        basis: 'per100',
        kcal: food.kcal100,
        protein: food.p100,
        fat: food.f100,
        carbs: food.c100,
        barcode: food.barcode,
        source: 'barcode',
        notes: incomplete ? 'Продукт найден, но КБЖУ неполные. Введите недостающие значения вручную с упаковки.' : null,
      };
      hapticSuccess();
      stop();
      navigate(`/nutrition/food?${query}`, { state: { prefill } });
    },
    [navigate, query, stop],
  );

  const lookup = useCallback(
    async (code: string) => {
      if (busy) return;
      setBusy(true);
      try {
        const result = await api.get<Food | { status: 'incomplete'; food: Parameters<typeof handleFound>[0] }>(`/api/foods/barcode/${code}`);
        if ('status' in result) handleFound(result.food, true);
        else handleFound(result);
      } catch (error) {
        scanning.current = false;
        if (error instanceof ApiError && error.status === 404) {
          setProblem({ kind: 'not_found', message: error.message, code });
        } else if (error instanceof ApiError && error.status === 503) {
          setProblem({ kind: 'unavailable', message: error.message, code });
        } else {
          setProblem({
            kind: 'unavailable',
            message: 'Не удалось проверить код — попробуйте позже или добавьте вручную',
            code,
          });
        }
        setBusy(false);
      }
    },
    [busy, handleFound],
  );

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      try {
        // Разрешение просим высокое: на штрихкод должно прийтись достаточно
        // пикселей, иначе полосы сливаются и распознавание буксует.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });

        // Непрерывный автофокус — там, где он поддерживается. Без него камера
        // часто держит фокус на дальнем плане, а код остаётся размытым.
        const track = stream.getVideoTracks()[0];
        if (track) {
          const caps = track.getCapabilities?.() as
            | { focusMode?: string[] }
            | undefined;
          if (caps?.focusMode?.includes('continuous')) {
            await track
              .applyConstraints({
                advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet],
              })
              .catch(() => undefined);
          }
        }
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
      } catch {
        if (!cancelled) {
          setCameraError('Камера недоступна — введите цифры штрихкода или добавьте вручную');
        }
      }
    };

    void start();
    return () => {
      cancelled = true;
      stop();
    };
  }, [stop]);

  // Движок распознавания поднимаем один раз: нативный, иначе ZXing (WASM).
  useEffect(() => {
    let cancelled = false;
    void createBarcodeReader().then((reader) => {
      if (cancelled) return;
      readerRef.current = reader;
      setEngine(reader.engine);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (mode !== 'barcode' || cameraError || problem) return;
    const reader = readerRef.current;
    if (!reader || reader.engine === 'none') return;

    scanning.current = true;
    let frame = 0;

    const tick = async () => {
      if (!scanning.current) return;
      const video = videoRef.current;
      if (video) {
        const value = await reader.read(video);
        if (value && isProductBarcode(value)) {
          scanning.current = false;
          await lookup(value);
          return;
        }
      }
      // Раньше здесь стояла пауза 300–500 мс, и она же была главным тормозом:
      // сам проход занимает 30–90 мс, а ждали мы впятеро дольше. Теперь следующий
      // кадр берём почти сразу — паузы хватает, чтобы не занять поток целиком.
      frame = window.setTimeout(() => void tick(), 60);
    };

    void tick();
    return () => {
      scanning.current = false;
      window.clearTimeout(frame);
    };
  }, [mode, cameraError, problem, lookup, engine]);

  const captureLabel = async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      toast('Камера ещё не готова');
      return;
    }
    setBusy(true);
    try {
      const canvas = document.createElement('canvas');
      const scale = Math.min(1, 1600 / Math.max(video.videoWidth, video.videoHeight));
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Не удалось обработать кадр');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      const base64 = dataUrl.split(',')[1] ?? '';

      const result = await api.post<{
        name: string | null;
        brand: string | null;
        basis: 'per100' | 'perServing';
        servingGrams: number | null;
        kcal: number | null;
        protein: number | null;
        fat: number | null;
        carbs: number | null;
        notes: string | null;
      }>('/api/foods/ocr', { imageBase64: base64, mediaType: 'image/jpeg' });

      const prefill: ScanPrefill = {
        name: result.name ?? '',
        brand: result.brand,
        grams: result.servingGrams ?? 100,
        basis: result.basis,
        kcal: result.kcal,
        protein: result.protein,
        fat: result.fat,
        carbs: result.carbs,
        source: 'ocr',
        notes: result.notes,
      };
      hapticSuccess();
      stop();
      navigate(`/nutrition/food?${query}`, { state: { prefill } });
    } catch (error) {
      setProblem({
        kind: 'ocr',
        message:
          error instanceof ApiError
            ? error.message
            : 'Не удалось распознать этикетку — добавьте вручную',
      });
      setBusy(false);
    }
  };

  const close = () => {
    stop();
    navigate(-1);
  };

  return (
    <div className="cam">
      <video ref={videoRef} playsInline muted autoPlay />
      {cameraError ? (
        <div className="cam-fallback">
          <p>{cameraError}</p>
        </div>
      ) : null}

      <div className="frame" aria-hidden="true">
        <s />
        <s />
        <s />
        <s />
        {mode === 'barcode' ? <em /> : null}
      </div>

      <div className="cam-top">
        <button className="round" type="button" onClick={close} aria-label="Закрыть">
          <CloseIcon />
        </button>
        <button className="round" type="button" aria-label="Вспышка" disabled>
          <FlashIcon />
        </button>
      </div>

      <p className="cam-hint">
        {mode === 'barcode' ? 'Наведите камеру на штрихкод' : 'Наведите камеру на таблицу КБЖУ'}
        <small>
          {mode !== 'barcode'
            ? 'Снимок уйдёт на распознавание'
            : engine === 'loading'
              ? 'Готовим распознавание…'
              : engine === 'none'
                ? 'Автораспознавание недоступно — введите цифры'
                : 'Распознаем автоматически'}
        </small>
      </p>

      <div className="cam-sheet">
        <div className="grab" />
        <div className="seg full" role="group" aria-label="Что сканируем">
          <button
            type="button"
            className={mode === 'barcode' ? 'on' : undefined}
            aria-pressed={mode === 'barcode'}
            onClick={() => setMode('barcode')}
          >
            Штрихкод
          </button>
          <button
            type="button"
            className={mode === 'label' ? 'on' : undefined}
            aria-pressed={mode === 'label'}
            onClick={() => setMode('label')}
          >
            Этикетка с КБЖУ
          </button>
        </div>

        {mode === 'label' ? (
          <button
            className="b p big"
            type="button"
            style={{ width: '100%', marginTop: 12 }}
            onClick={() => void captureLabel()}
            disabled={busy || Boolean(cameraError)}
          >
            {busy ? 'Распознаём…' : 'Сделать снимок'}
          </button>
        ) : (
          <div style={{ marginTop: 8 }}>
            {manualOpen ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0' }}>
                <input
                  className="f"
                  style={{ marginTop: 0 }}
                  inputMode="numeric"
                  placeholder="4600000000000"
                  value={manualCode}
                  onChange={(event) => setManualCode(event.target.value.replace(/\D/g, ''))}
                  autoFocus
                />
                <button
                  className="b p"
                  type="button"
                  disabled={manualCode.length < 8 || busy}
                  onClick={() => void lookup(manualCode)}
                >
                  Найти
                </button>
              </div>
            ) : (
              <button className="lnk" type="button" onClick={() => setManualOpen(true)}>
                Ввести цифры штрихкода <span className="mu">›</span>
              </button>
            )}
            <button
              className="lnk"
              type="button"
              onClick={() => {
                stop();
                navigate(`/nutrition/food?${query}`);
              }}
            >
              Добавить вручную <span className="mu">›</span>
            </button>
          </div>
        )}
      </div>

      {problem ? (
        <div className="overlay" onClick={() => setProblem(null)}>
          <div
            className="sheet"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="grab" />
            <h2>
              {problem.kind === 'not_found' ? 'Не нашли продукт' : 'Каталог недоступен'}
            </h2>
            <p className="nt" style={{ margin: '6px 0 16px' }}>
              {problem.code ? (
                <>
                  Кода <span className="code">{problem.code}</span>{' '}
                </>
              ) : null}
              {problem.message}
              {problem.kind === 'not_found'
                ? '. Добавьте продукт вручную — мы запомним его для следующего раза.'
                : ''}
            </p>
            <div style={{ display: 'grid', gap: 8 }}>
              <button
                className="b p"
                type="button"
                onClick={() => {
                  stop();
                  navigate(`/nutrition/food?${query}`, {
                    state: problem.code
                      ? {
                          prefill: {
                            name: '',
                            brand: null,
                            grams: 100,
                            basis: 'per100',
                            kcal: null,
                            protein: null,
                            fat: null,
                            carbs: null,
                            barcode: problem.code,
                            source: 'barcode',
                          } satisfies ScanPrefill,
                        }
                      : undefined,
                  });
                }}
              >
                Добавить вручную
              </button>
              <button
                className="b"
                type="button"
                onClick={() => {
                  setProblem(null);
                  setManualCode('');
                }}
              >
                Сканировать ещё раз
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
